import React, { useEffect, useState } from "react";
import { PuffLoader } from "react-spinners";
import DOMPurify from "dompurify";

// Large sheets would otherwise render tens of thousands of DOM nodes in the modal
const MAX_SHEET_ROWS = 1000;

// Previewable types beyond PDF/images (those are rendered natively by the caller)
export const getPreviewType = (contentType) => {
  if (!contentType) return null;
  const ct = contentType.toLowerCase();
  if (ct.includes("csv")) return "csv";
  if (ct.includes("spreadsheetml") || ct.includes("xlsx")) return "xlsx";
  if (ct.includes("wordprocessingml") || ct.includes("docx")) return "docx";
  if (ct.startsWith("video/") || ct.includes("mp4") || ct.includes("webm")) return "video";
  if (ct.startsWith("audio/") || ct.includes("mpeg") || ct.includes("mp3")) return "audio";
  return null;
};

const SheetTable = ({ name, rows, truncated }) => (
  <div className="mb-4">
    {name && <h4 className="text-sm font-semibold text-gray-700 mb-1">{name}</h4>}
    <div className="overflow-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs text-gray-800">
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className={r === 0 ? "bg-gray-50 font-semibold" : "border-t border-gray-100"}>
              {row.map((cell, c) => (
                <td key={c} className="px-2 py-1 whitespace-nowrap">{String(cell ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {truncated && <p className="text-xs text-gray-500 mt-1">Showing first {MAX_SHEET_ROWS} rows.</p>}
  </div>
);

// Fetches and renders docx / xlsx / csv in the browser. Libraries are loaded on demand
// so the EMS lookup bundle doesn't carry them for users who never open a document.
const DocumentPreview = ({ url, type, title }) => {
  const [state, setState] = useState({ loading: true, error: null, html: null, sheets: null });

  useEffect(() => {
    if (type === "video" || type === "audio") return undefined;
    let cancelled = false;
    setState({ loading: true, error: null, html: null, sheets: null });

    (async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (type === "docx") {
        const { default: mammoth } = await import("mammoth");
        const { value } = await mammoth.convertToHtml({ arrayBuffer: await res.arrayBuffer() });
        return { html: DOMPurify.sanitize(value) };
      }
      const XLSX = await import("xlsx");
      const workbook =
        type === "csv"
          ? XLSX.read(await res.text(), { type: "string" })
          : XLSX.read(await res.arrayBuffer(), { type: "array" });
      const sheets = workbook.SheetNames.map((name) => {
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: "" });
        return { name, rows: rows.slice(0, MAX_SHEET_ROWS), truncated: rows.length > MAX_SHEET_ROWS };
      });
      return { sheets };
    })()
      .then((result) => !cancelled && setState({ loading: false, error: null, html: null, sheets: null, ...result }))
      .catch((e) => {
        console.warn("[DocumentPreview]", e);
        if (!cancelled) setState({ loading: false, error: e, html: null, sheets: null });
      });

    return () => {
      cancelled = true;
    };
  }, [url, type]);

  if (type === "video") {
    return (
      <video src={url} controls controlsList="nodownload" className="max-w-full max-h-[80vh]">
        {title}
      </video>
    );
  }
  if (type === "audio") {
    return <audio src={url} controls controlsList="nodownload" className="w-full max-w-md" />;
  }
  if (state.loading) {
    return <PuffLoader size={48} color="#6b7280" />;
  }
  if (state.error) {
    return <p className="text-gray-500">This file could not be displayed in the viewer.</p>;
  }
  if (state.html !== null) {
    return (
      <div
        className="w-full h-full min-h-[60vh] overflow-auto bg-white rounded-lg p-4 text-sm text-gray-800"
        dangerouslySetInnerHTML={{ __html: state.html }}
      />
    );
  }
  return (
    <div className="w-full h-full min-h-[60vh] overflow-auto bg-white rounded-lg p-3">
      {state.sheets.map((sheet) => (
        <SheetTable
          key={sheet.name}
          name={state.sheets.length > 1 ? sheet.name : null}
          rows={sheet.rows}
          truncated={sheet.truncated}
        />
      ))}
    </div>
  );
};

export default DocumentPreview;
